/**
 * CR V3 课后 QR：语言匹配 (D23)、speech/minutes 投影、抽屉可见性
 */

import { shortLangCode } from '../adapters/live-speech-adapter'
import type {
  CrV3ProjectionContentItem,
  CrV3SessionSnapshot,
  CrV3TargetResourceItem,
} from '../types'

export type PostClassQrKind = 'speech' | 'minutes' | 'courseware'

export interface PostClassQrItem {
  id: string
  kind: PostClassQrKind
  label: string
  url: string
}

export interface PostClassHints {
  pendingMinutes: boolean
  pendingMinutesText: string
  partialFailure: boolean
  partialFailureText: string
  waitingEnded: boolean
  waitingEndedText: string
}

const POST_CLASS_KEYS = [
  'status',
  'canDownloadResource',
  'canDownloadSpeech',
  'canDownloadMinutes',
  'speechContent',
  'minutesContent',
  'targetResource',
  'minutesTranslateStatus',
  'failedLangCodes',
] as const

/** D23：短码或 BCP47 code2 任一相等即匹配 */
export function langCodesMatch(a: string, b: string): boolean {
  const left = (a || '').trim().toLowerCase()
  const right = (b || '').trim().toLowerCase()
  if (!left || !right) {
    return false
  }
  if (left === right) {
    return true
  }
  return shortLangCode(left) === shortLangCode(right)
}

export function matchesViewerLang(workLangCode: string | undefined, viewerLangCodes: string[]): boolean {
  if (!workLangCode) {
    return false
  }
  return viewerLangCodes.some((code) => langCodesMatch(workLangCode, code))
}

/** 浅合并课后字段；缺省 key 保留 held（布尔 false 视为显式） */
export function mergePostClassSessionFields(
  held: CrV3SessionSnapshot,
  patch: CrV3SessionSnapshot,
): CrV3SessionSnapshot {
  const next: CrV3SessionSnapshot = { ...held }

  if (patch.sourceLanguage != null) {
    next.sourceLanguage = patch.sourceLanguage
  }
  if (patch.targetLanguage != null) {
    next.targetLanguage = patch.targetLanguage
  }
  if (patch.workLangCode != null) {
    next.workLangCode = patch.workLangCode
  }
  if (patch.workLangListenCode != null) {
    next.workLangListenCode = patch.workLangListenCode
  }
  if (patch.speakMode != null) {
    next.speakMode = patch.speakMode
  }
  if (patch.canParticipantSpeak != null) {
    next.canParticipantSpeak = patch.canParticipantSpeak
  }
  if (patch.participant != null) {
    next.participant = patch.participant
  }
  if (patch.subtitleStyle != null) {
    next.subtitleStyle = patch.subtitleStyle
  }
  if (patch.active != null) {
    next.active = patch.active
  }

  for (const key of POST_CLASS_KEYS) {
    if (Object.prototype.hasOwnProperty.call(patch, key)) {
      const value = patch[key]
      if (value !== undefined) {
        Object.assign(next, { [key]: value })
      }
    }
  }

  return next
}

export function shouldShowPostClassDrawer(session: CrV3SessionSnapshot): boolean {
  if (
    session.canDownloadSpeech === true
    || session.canDownloadMinutes === true
    || session.canDownloadResource === true
  ) {
    return true
  }
  return (session.status || '').toLowerCase() === 'ended'
}

function appendProjectionQrItems(
  items: PostClassQrItem[],
  rows: CrV3ProjectionContentItem[] | undefined,
  kind: 'speech' | 'minutes',
  label: string,
  viewerLangCodes: string[],
): void {
  if (!Array.isArray(rows)) {
    return
  }
  rows.forEach((row, index) => {
    const path = (row.workContentPath || '').trim()
    if (!path || !matchesViewerLang(row.workLangCode, viewerLangCodes)) {
      return
    }
    items.push({
      id: `${kind}-${row.workLangCode ?? index}-${index}`,
      kind,
      label,
      url: path,
    })
  })
}

export function buildPostClassQrItems(
  session: CrV3SessionSnapshot,
  viewerLangCodes: string[],
  _sourceCode: string,
): PostClassQrItem[] {
  const items: PostClassQrItem[] = []
  const codes = viewerLangCodes.filter(Boolean)

  if (session.canDownloadSpeech === true) {
    appendProjectionQrItems(
      items,
      session.speechContent,
      'speech',
      '课堂记录 / Speech History',
      codes,
    )
  }

  if (session.canDownloadMinutes === true) {
    appendProjectionQrItems(
      items,
      session.minutesContent,
      'minutes',
      '会议纪要 / Meeting Minutes',
      codes,
    )
  }

  if (session.canDownloadResource === true && Array.isArray(session.targetResource)) {
    session.targetResource.forEach((row: CrV3TargetResourceItem, index: number) => {
      const path = (row.transDocPath || '').trim()
      if (!path) {
        return
      }
      if (!matchesViewerLang(row.transToCode, codes)) {
        return
      }
      const name = (row.transDocName || row.transDocToName || '').trim()
      items.push({
        id: `resource-${row.crWorkDocId ?? index}-${row.transToCode ?? index}`,
        kind: 'courseware',
        label: name ? `课件 / Courseware — ${name}` : '课件 / Courseware',
        url: path,
      })
    })
  }

  return items
}

function hasMinutesQrForViewer(
  session: CrV3SessionSnapshot,
  viewerLangCodes: string[],
): boolean {
  if (session.canDownloadMinutes !== true || !Array.isArray(session.minutesContent)) {
    return false
  }
  return session.minutesContent.some((row) => {
    if (!(row.workContentPath || '').trim()) {
      return false
    }
    return matchesViewerLang(row.workLangCode, viewerLangCodes)
  })
}

export function resolvePostClassHints(
  session: CrV3SessionSnapshot,
  viewerLangCodes: string[],
  _sourceCode: string,
): PostClassHints {
  const ended = (session.status || '').toLowerCase() === 'ended'
  const minutesAllowed = session.canDownloadMinutes === true
  const hasMinutes = hasMinutesQrForViewer(session, viewerLangCodes)
  const translateStatus = session.minutesTranslateStatus
  const pendingMinutes =
    minutesAllowed
    && !hasMinutes
    && translateStatus === 0

  const failed = Array.isArray(session.failedLangCodes)
    ? session.failedLangCodes.filter(Boolean)
    : []
  const partialFailure = minutesAllowed && translateStatus === 3 && failed.length > 0

  const waitingEnded =
    ended
    && session.canDownloadSpeech !== true
    && !minutesAllowed
    && session.canDownloadResource !== true

  return {
    pendingMinutes,
    pendingMinutesText: pendingMinutes ? '会议纪要生成中…' : '',
    partialFailure,
    partialFailureText: partialFailure
      ? `部分语种纪要翻译失败：${failed.join(', ')}`
      : '',
    waitingEnded,
    waitingEndedText: waitingEnded ? '课程已结束，课后资料准备中…' : '',
  }
}
